import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography,
} from '@mui/material';
import { formatDistance } from 'date-fns';
import {
    RecordContextProvider,
    ReferenceManyField,
    ShowBase,
    SortButton,
    TabbedShowLayout,
    useListContext,
    useRecordContext,
    useShowContext,
} from 'react-admin';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { FundChart } from "./FundChart";

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { Status } from '../misc/Status';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import { Fund, Position } from '../../types/types';
import { FundAside } from './FundAside';
import { useEffect, useState } from "react";
import { useDataProvider, useGetIdentity } from "react-admin";

export const FundShow = () => (
    <ShowBase>
        <FundShowContent />
    </ShowBase>
);

const FundShowContent = () => {
    const { record, isPending } = useShowContext<Fund>();
    const [position, setPosition] = useState<Position | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const dataProvider = useDataProvider();

    useEffect(() => {
        if (!record || !record.id) return;

        const fetchPosition = async () => {
            setLoading(true);
            try {
                const response = await dataProvider.getList('positions', {
                    filter: { asset_id: record.id },  // Assuming `asset` matches `fund.id`
                    pagination: { page: 1, perPage: 1 },  // Fetch only one match
                    sort: { field: 'timestamp', order: 'DESC' }
                });

                if (response && response.data.length > 0) {
                    setPosition(response.data[0]);  // Assuming we take the first match
                }
            } catch (error) {
                console.error("❌ Error fetching position data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPosition();
    }, [record, dataProvider]);

    if (isPending || !record) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box mt={2} display="flex">
            <Box flex="1">
                <Card>
                    <CardContent sx={{
                        "&:last-child": { paddingBottom: 0 },
                    }}>
                        <Box display="flex" alignItems="center" gap={0} mb={1}>
                            <ArrowForwardIosIcon color="primary" />
                            <Typography variant="h5" ml={2} flex="1">
                                {record.name}
                            </Typography>
                        </Box>

                        <TabbedShowLayout
                            sx={{ '& .RaTabbedShowLayout-content': { p: 0 } }}
                        >
                            <TabbedShowLayout.Tab label="Information">
                                <InformationTab fund={record} />
                            </TabbedShowLayout.Tab>
                            <TabbedShowLayout.Tab label="Data">
                                <DataTab fund={record} />
                            </TabbedShowLayout.Tab>
                            <TabbedShowLayout.Tab label="Activity">
                                <ActivityTab fund={record} />
                            </TabbedShowLayout.Tab>
                        </TabbedShowLayout>
                    </CardContent>
                </Card>
            </Box>
            <FundAside position={position} />
        </Box>
    );
};

interface FundProps {
    fund: Fund;
}

const InformationTab = ({ fund }: FundProps) => (
    <Box
        display="flex"
        flexDirection="column"
        sx={{ paddingBottom: '0' }}
    >
        <Box
            sx={{
                mb: 2,
                mt: 2,
                px: 0,
            }}
        >
            <Grid container spacing={2}>
                {/* Corporation */}
                <Grid item xs={12} sm={3} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon fontSize="small" color="primary" />
                        <Typography variant="caption" color="textSecondary">Corp:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {fund.corporation || "Aktiengesellschaft"}
                        </Typography>
                    </Box>
                </Grid>

                {/* Country */}
                <Grid item xs={12} sm={3} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <PublicIcon fontSize="small" color="primary" />
                        <Typography variant="caption" color="textSecondary">Country:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {fund.country || "Liechtenstein"}
                        </Typography>
                    </Box>
                </Grid>

                {/* Capital / Volume */}
                <Grid item xs={12} sm={3} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <AccountBalanceIcon fontSize="small" color="primary" />
                        <Typography variant="caption" color="textSecondary">NAV:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {/* fund.capital ? `${fund.capital.toLocaleString()} EUR` : "N/A" */}
                            100000.0 EUR
                        </Typography>
                    </Box>
                </Grid>

                {/* Status - align right on desktop */}
                <Grid item xs={12} sm={3} md={3}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        {fund.status?.toLowerCase() === "open" ? (
                            <CheckCircleOutlineIcon fontSize="small" sx={{ color: "success.main" }} />
                        ) : (
                            <LockOutlinedIcon fontSize="small" sx={{ color: "warning.main" }} />
                        )}
                        <Typography variant="caption" color="textSecondary">Status:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {fund.status
                                ? fund.status.charAt(0).toUpperCase() + fund.status.slice(1).toLowerCase()
                                : "Open"}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
        {fund.description && (
            <Box px={0} sx={{
                '& > *:first-of-type': {
                    marginTop: 0, // removes top margin from first paragraph/heading
                },
                '& p': {
                    marginTop: 0, // optional: collapse all p margins if needed
                },
            }}>
                <ReactMarkdown
                    components={{
                        p: ({ children }) => (
                            <Typography variant="body2" color="textSecondary" paragraph>
                                {children}
                            </Typography>
                        ),
                        h1: ({ children }) => (
                            <Typography variant="h5" color="textPrimary" gutterBottom>
                                {children}
                            </Typography>
                        ),
                        h2: ({ children }) => (
                            <Typography variant="h6" color="textPrimary" gutterBottom>
                                {children}
                            </Typography>
                        ),
                        li: ({ children }) => (
                            <li>
                                <Typography variant="body2" color="textSecondary" component="span">
                                    {children}
                                </Typography>
                            </li>
                        ),
                    }}
                >
                    {fund.description}
                </ReactMarkdown>
            </Box>
        )}
    </Box>
);

const DataTab = ({ fund }: FundProps) => (
    <Box
        display="flex"
        flexDirection="column"
        sx={{
            height: 320,
            paddingBottom: '0',
        }}
    >
        <FundChart fund={fund} />
    </Box>
);

const ActivityTab = ({ fund }: FundProps) => (
    <Box
        display="flex"
        flexDirection="column"
        sx={{
            height: 320,
            paddingBottom: '0',
        }}
    >
        <FundChart fund={fund} />
    </Box>
);